import { useNavigate } from '#node_modules/@remix-run/react';
import { useSearchParams } from '@remix-run/react';
import { format } from 'date-fns-tz';
import { useCallback } from 'react';
import { type DateRange } from 'react-day-picker';
import { CSVConverter } from '#app/utils/json2csv';

export function useDashboardSettings(
  dashboardSlug: string,
  options: {
    startDate: string;
    endDate: string;
  },
): {
  onSlugChange: (slug: string) => void;
  onSelectExport: (
    widgetType: string,
    widgetId: string,
    format: string,
    data: unknown,
  ) => void;
  onSetDate: (date: Date | undefined) => void;
  onSetDateRange: (date: DateRange | undefined) => void;
  isDev: boolean;
} {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();

  const onSelectExport = useCallback(
    (widgetType: string, widgetId: string, format: string, data: unknown) => {
      if (format === 'csv') {
        const csv = CSVConverter.convert(data);
        CSVConverter.download(csv, `${widgetId}.csv`);
        return;
      }
      const query: URLSearchParams | null = new URLSearchParams({
        widgetId,
        widgetType,
        dashboardSlug,
        theme: 'light',
        startDate: options.startDate,
        endDate: options.endDate,
      });

      if (format === 'png') {
        query.set('type', 'png');
      }

      if (format === 'png@2x') {
        query.set('type', 'png');
        query.set('scaleFactor', '2');
      }

      if (format === 'pdf') {
        query.set('type', 'pdf');
      }

      const href = `${window.location.origin}/resources/download-widget?${query.toString()}`;
      window.open(href, '_blank', 'noreferrer noopener');
    },
    [dashboardSlug, options.startDate, options.endDate],
  );

  const onSetDate = (date: Date | undefined) => {
    if (!date) return;

    const formattedDate = format(date, 'yyyy-MM-dd', {
      timeZone: 'Europe/Madrid',
    });
    setSearchParams(
      {
        date: formattedDate,
      },
      { preventScrollReset: true },
    );
  };

  const onSetDateRange = (date: DateRange | undefined) => {
    if (!date) return;

    if (date.from == null || date.to == null) {
      return;
    }
    const formattedStartDate = format(date.from, 'yyyy-MM-dd', {
      timeZone: 'Europe/Madrid',
    });
    const formattedEndDate = format(date.to, 'yyyy-MM-dd', {
      timeZone: 'Europe/Madrid',
    });

    setSearchParams(
      {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
      { preventScrollReset: true },
    );
  };

  const onSlugChange = (slug: string) => {
    navigate(`${slug}?${searchParams}`);
  };

  const isDev =
    typeof window === 'undefined'
      ? process.env.NODE_ENV === 'development'
      : window.ENV.MODE === 'development';

  return { onSelectExport, onSetDate, onSetDateRange, isDev, onSlugChange };
}
